import { computed, ref, watch } from 'vue'
import type { Ref } from 'vue'
import type { useGuidedModeStore } from '../stores/guidedMode'
import type { GuidedAnswer, GuidedAssessmentPath, GuidedModeSymptom } from '../types/guided-mode.types'
import { availableSymptoms, isSymptomRecorded, recordSymptoms } from '../utils/symptoms'

interface GuidedSymptomsSnapshot {
  selectedSymptomIds: string[]
  otherSymptomSelected: boolean
  comprehensiveSelected: boolean
}

interface UseGuidedSymptomsOptions {
  store: ReturnType<typeof useGuidedModeStore>
  answers: Ref<Record<string, GuidedAnswer>>
  activeAssessmentPath: Ref<GuidedAssessmentPath | null>
}

export function useGuidedSymptoms({ store, answers, activeAssessmentPath }: UseGuidedSymptomsOptions) {
  const trackSymptoms = computed<GuidedModeSymptom[]>(() => (
    activeAssessmentPath.value ? store.clinicalContent.symptoms?.[activeAssessmentPath.value] || [] : []
  ))
  const symptoms = computed(() => availableSymptoms(trackSymptoms.value, answers.value))
  const selectedSymptomIds = computed(() => {
    const availableIds = new Set(symptoms.value.map(symptom => symptom.id))
    return store.selectedSymptomIds.filter(id => availableIds.has(id))
  })
  const otherSymptomSelected = computed(() => store.otherSymptomSelected)
  const comprehensiveSelected = computed(() => store.comprehensiveSelected)
  const canContinue = computed(() => (
    selectedSymptomIds.value.length > 0 || store.otherSymptomSelected || store.comprehensiveSelected
  ))
  const editing = ref(false)
  let snapshot: GuidedSymptomsSnapshot | null = null
  // A recorded answer that clearStaleAnswers removed no longer excludes its question.
  const startedFocusedAssessments = computed(() => store.startedFocusedAssessments)
  const consultationPath = computed(() => store.consultationPath)
  const droppedSymptomIds = computed(() => trackSymptoms.value
    .filter(symptom => store.recordedSymptomIds.includes(symptom.id) && !isSymptomRecorded(symptom, answers.value))
    .map(symptom => symptom.id))
  // The questionnaire owns a dropped symptom's question, so a later Next must not record it again.
  watch(droppedSymptomIds, ids => {
    if (!ids.length) return
    store.recordedSymptomIds = store.recordedSymptomIds.filter(id => !ids.includes(id))
    store.selectedSymptomIds = store.selectedSymptomIds.filter(id => !ids.includes(id))
  })
  const answeredSymptoms = computed(() => startedFocusedAssessments.value
    .filter(symptom => !symptom.selectionOnly))
  const recordedDataPointCodes = computed(() => answeredSymptoms.value.map(symptom => symptom.code))
  const recordedConceptIds = computed(() => answeredSymptoms.value
    .map(symptom => symptom.conceptId))
  const keptConceptIds = computed(() => startedFocusedAssessments.value
    .filter(symptom => symptom.keepWhenHidden)
    .map(symptom => symptom.conceptId))

  function toggleSymptom(id: string) {
    store.selectedSymptomIds = selectedSymptomIds.value.includes(id)
      ? store.selectedSymptomIds.filter(selectedId => selectedId !== id)
      : [...selectedSymptomIds.value, id]
  }
  function setOtherSymptom(selected: boolean) {
    store.otherSymptomSelected = selected
    if (selected) store.comprehensiveSelected = true
  }
  function setComprehensive(selected: boolean) {
    store.comprehensiveSelected = selected
    if (!selected) store.otherSymptomSelected = false
  }
  function recordSelections(previousAnswers?: Record<string, GuidedAnswer>) {
    const result = recordSymptoms({
      answers: answers.value,
      previousAnswers,
      trackSymptoms: trackSymptoms.value,
      available: symptoms.value,
      selectedIds: store.selectedSymptomIds,
      recordedIds: store.recordedSymptomIds,
    })
    store.selectedSymptomIds = result.selectedIds
    store.recordedSymptomIds = result.recordedIds
    answers.value = result.answers
  }
  function restartSelections() {
    store.selectedSymptomIds = selectedSymptomIds.value
    store.recordedSymptomIds = []
    acceptEdit()
  }
  function beginEdit() {
    snapshot = {
      selectedSymptomIds: [...store.selectedSymptomIds],
      otherSymptomSelected: store.otherSymptomSelected,
      comprehensiveSelected: store.comprehensiveSelected,
    }
    editing.value = true
  }
  function acceptEdit() {
    editing.value = false
    snapshot = null
  }
  function cancelEdit() {
    if (snapshot) {
      store.selectedSymptomIds = snapshot.selectedSymptomIds
      store.otherSymptomSelected = snapshot.otherSymptomSelected
      store.comprehensiveSelected = snapshot.comprehensiveSelected
    }
    acceptEdit()
  }

  return {
    symptoms, selectedSymptomIds, otherSymptomSelected, comprehensiveSelected, canContinue, editing,
    startedFocusedAssessments, consultationPath, recordedDataPointCodes, recordedConceptIds,
    keptConceptIds, toggleSymptom, setOtherSymptom, setComprehensive, recordSelections, restartSelections,
    beginEdit, acceptEdit, cancelEdit,
  }
}
